import { useState, useCallback, useEffect } from 'react';
import { vendorService, handleVendorApiError, createPaginationParams, createSearchFilters } from '../services/vendorService';
import { toast } from 'react-hot-toast';

export const useVendorApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch applications
  const fetchApplications = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = createPaginationParams(page, itemsPerPage, createSearchFilters(
        filters.search || '',
        filters.status || 'all',
        filters.category || ''
      ));
      
      const response = await vendorService.getAllApplications(params);
      
      if (response.success) {
        setApplications(response.data.applications || []);
        setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
      } else {
        setError(response.message || 'Failed to fetch applications');
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      const errorMessage = handleVendorApiError(error);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [page, itemsPerPage]);

  // Approve application
  const approveApplication = useCallback(async (vendorId, approvalData = {}) => {
    try {
      const response = await vendorService.approveApplication(vendorId, approvalData);
      
      if (response.success) {
        toast.success('Vendor application approved successfully!');
        return { success: true, data: response.data };
      } else {
        toast.error(response.message || 'Approval failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      const errorMessage = handleVendorApiError(error);
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, []);

  // Reject application
  const rejectApplication = useCallback(async (vendorId, rejectionData = {}) => {
    try {
      const response = await vendorService.rejectApplication(vendorId, rejectionData);
      
      if (response.success) {
        toast.success('Vendor application rejected successfully!');
        return { success: true, data: response.data };
      } else {
        toast.error(response.message || 'Rejection failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      const errorMessage = handleVendorApiError(error);
      toast.error(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, []);

  // Refresh applications
  const refreshApplications = useCallback((filters = {}) => {
    fetchApplications(filters);
  }, [fetchApplications]);

  // Change page
  const changePage = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  // Reset to first page
  const resetToFirstPage = useCallback(() => {
    setPage(1);
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Get application by ID
  const getApplicationById = useCallback((id) => {
    return applications.find(app => app._id === id);
  }, [applications]);

  // Filter applications by status
  const getApplicationsByStatus = useCallback((status) => {
    if (status === 'all') return applications;
    return applications.filter(app => app.status === status);
  }, [applications]);

  // Search applications
  const searchApplications = useCallback((searchTerm) => {
    if (!searchTerm) return applications;
    const term = searchTerm.toLowerCase();
    return applications.filter(app => 
      app.businessName?.toLowerCase().includes(term) ||
      app.businessDescription?.toLowerCase().includes(term) ||
      app.contactEmail?.toLowerCase().includes(term) ||
      app.contactPhone?.includes(term)
    );
  }, [applications]);

  // Get statistics
  const getStats = useCallback(() => {
    const total = applications.length;
    const pending = applications.filter(app => app.status === 'pending').length;
    const approved = applications.filter(app => app.status === 'approved').length;
    const rejected = applications.filter(app => app.status === 'rejected').length;

    return {
      total,
      pending,
      approved,
      rejected,
      approvalRate: total > 0 ? ((approved / total) * 100).toFixed(1) : 0
    };
  }, [applications]);

  return {
    // State
    applications,
    loading,
    error,
    page,
    totalPages,
    itemsPerPage,
    
    // Actions
    fetchApplications,
    approveApplication,
    rejectApplication,
    refreshApplications,
    changePage,
    resetToFirstPage,
    clearError,
    
    // Utilities
    getApplicationById,
    getApplicationsByStatus,
    searchApplications,
    getStats
  };
}; 